import { Flex } from "@chakra-ui/core";
import { Button } from "components/Button";
import { TabHeader } from "components/TabHeader";
import { useEffect, useState } from "react";
import FilterButton from "components/FuncButton/FilterButton";
import ButtonViewGroup from "components/ButtonViewGroup/ButtonViewGroup";
import { useDisclosure } from "@chakra-ui/core";
import { connect } from "react-redux";
import tw, { styled } from "twin.macro";
import RFQGridView from "./Views/GridView/RFQGridView";
import { FilterRFQ } from "components/Pages/RFQ/FilterRFQ";
import { RFQListView } from "./Views/ListView";
import { CreateRFxSlider } from "newui/features/Rfx/Creation/CreateRFxSlider";
import { useSelector } from "react-redux";
import SearchBar from "components/RFQ/SearchBar/SearchBar";
import { getRfqPerPage } from "actions/RFQ/rfqsAction";
import BadgeComp from "components/RFQ/BadgeComp/BadgeComp";
import { useDispatch } from "react-redux";
import { SET_RFQ_BADGES_COUNT } from "actions/RFQ/actionTypes";
import SortComp from "components/RFQ/SortComp/SortComp";
import { Icon } from "assets/Icon";
import { useProfileContext } from "newui/features/Auth/context/ProfileContext";
import { navigate } from "@reach/router";
import { MyTemplatesButton } from "pages/RFQ/Templates/_components/MyTemplatesButton";
import { useCurrentCompanyId } from "hooks/auth";
import { Pagination } from "newui/components/Pagination";
import { useIsMenuCollapsed } from "newui/hooks/useMenuStatus";
import { useNavigationPaths } from "newui/features/Auth/hooks/useNavigationPaths";
import { ReadOnlyGuard } from "newui/features/ExecutiveView";

const RFQStyled = styled.div([tw`overflow-visible`]);

const sortedData = [
	{
		name: "Sort Alphabetically (A to Z)",
		value: "asc",
		sort: "Sort by: A to Z",
	},
	{
		name: "Sort Alphabetically (Z to A)",
		value: "desc",
		sort: "Sort by: Z to A",
	},
	{
		name: "Sort by Last Activity",
		value: "last_activity",
		sort: "Sort by: Activity",
	},
	{
		name: "Sort by Date Created",
		value: "created",
		sort: "Sort by: Date",
	},
];

const RFQ = ({ rfq, getRfqPerPage }) => {
	const { clientPaths } = useNavigationPaths();
	const [currentTab, setCurrentTab] = useState("active");
	const currentPage = useSelector(
		(state) => state.rfq?.rfqs?.rfqs?.[currentTab]?.meta?.current_page
	);
	const totalItems = useSelector(
		(state) => state.rfq?.rfqs?.rfqs?.[currentTab]?.meta?.total ?? 0
	);
	const perPage = useSelector(
		(state) => state.rfq?.rfqs?.rfqs?.[currentTab]?.meta?.per_page ?? 12
	);
	const [view, setView] = useState("grid");
	const [sorted, setSorted] = useState(sortedData[2]);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [openPopUp, setOpenPopUp] = useState(false);
	const [modalType, setModalType] = useState("filter");
	const [search, setSearch] = useState("");
	const [filters, setFilters] = useState(null);
	const [prevValues, setPrevValues] = useState(null);
	const isSidebarCollapsed = useIsMenuCollapsed();
	const rfqBadgeCounts = useSelector((state) => state.rfq?.rfqs?.rfqCount);
	const { currentProfile } = useProfileContext();
	const currentCompanyId = useCurrentCompanyId();

	const dispatch = useDispatch();

	const [currPage, setCurrPage] = useState(1);
	const [pageSize, setPageSize] = useState(12);

	const tabMenu = [
		{
			id: "active",
			name: "Current RFx",
			showBadge: true,
		},
		{
			id: "evaluation",
			name: "Evaluation",
			showBadge: true,
		},
		{
			id: "draft",
			name: "Drafts",
			showBadge: true,
		},
		{
			id: "archived",
			name: "Archived",
			showBadge: true,
		},
	];

	useEffect(() => {
		if (currentPage) {
			setCurrPage(currentPage);
		}
	}, [currentPage]);

	useEffect(() => {
		setCurrPage(1);
		getRfqPerPage({
			page: 1,
			limit: pageSize,
			search,
			sort: sorted.value,
			filters,
			reset: true,
		});
	}, [search, sorted, filters, currentTab, getRfqPerPage, pageSize]);

	useEffect(() => {
		const currentTotal = rfq?.[currentTab]?.meta?.total;
		if (currentTotal !== undefined && currentTotal !== null) {
			const updatedBadgeCounts = {
				...rfqBadgeCounts,
				[currentTab]: currentTotal,
			};
			dispatch({ type: SET_RFQ_BADGES_COUNT, payload: updatedBadgeCounts });
		}
	}, [search, sorted, filters, currentTab]);

	const handlePageChange = (newPage) => {
		setCurrPage(newPage);
		getRfqPerPage({
			page: newPage,
			limit: pageSize,
			search,
			sort: sorted.value,
			filters,
			reset: true,
		});
	};

	const handleRowsPerPageChange = (newSize) => {
		setPageSize(newSize);
		setCurrPage(1);
		getRfqPerPage({
			page: 1,
			limit: newSize,
			search,
			sort: sorted.value,
			filters,
			reset: true,
		});
	};

	const refreshData = () => {
		setCurrPage(1);
		getRfqPerPage({
			page: 1,
			search,
			sort: sorted.value,
			filters,
			reset: true,
		});
	};

	const handleCreateTemplate = async () => {
		try {
			navigate(clientPaths.rfx.createTemplate);
		} catch (error) {
			console.error("Template creation error:", error);
		}
	};

	const pagination =
		<Pagination
			currentPage={currPage}
			rowsPerPage={perPage}
			rowsPerPageOptions={[12, 24, 36]}
			totalItems={totalItems}
			onPageChange={handlePageChange}
			onRowsPerPageChange={handleRowsPerPageChange}
		/>;

	return (
		<RFQStyled>
			<TabHeader heading={`RFx: ${currentProfile}`}>
				<div className="flex gap-6">
					<ReadOnlyGuard>
						<MyTemplatesButton
							handleCreateTemplate={handleCreateTemplate}
							companyId={currentCompanyId}
							className="relative -top-[3px]"
						/>
					</ReadOnlyGuard>
					<ReadOnlyGuard>
						<Button
							style={{ height: "50px" }}
							onClick={() => {
								setModalType("createRFQ");
								onOpen();
							}}
							data-testid="create-rfq-button"
						>
							<Flex className="items-center gap-[6px]">
								<div className="flex h-5 w-5 items-center justify-center">
									<Icon
										icon="plus"
										style={{
											width: "12px",
											height: "12px",
											color: "#fff",
											fontWeight: "bold",
										}}
									/>
								</div>
								<div className="font-roboto text-base font-semibold capitalize">
									Create RFx
								</div>
							</Flex>
						</Button>
					</ReadOnlyGuard>
				</div>
			</TabHeader>
			<div id="layout-dashboard">
				<div className="grid w-full place-content-between gap-8 md:grid-cols-1 lg:grid-cols-2 lg:gap-0">
					<Flex className="scrollbar order-2 gap-2 overflow-x-auto overflow-y-hidden pb-3 lg:order-1 lg:pb-0">
						{tabMenu.map((tbMenu, index) => {
							return (
								<div
									key={index}
									className="group"
								>
									<Button
										btntype="tab"
										active={currentTab === tbMenu.id ? true : false}
										onClick={() => setCurrentTab(tbMenu.id)}
										data-testid={tbMenu.id}
									>
										<div
											style={{ fontFamily: "roboto", fontWeight: "500" }}
											className={`text-${currentTab === tbMenu.id ? "to-utility-brand-700" : "text-primary-500"}`}
										>
											{tbMenu.name}
										</div>
										{tbMenu.showBadge && (
											<BadgeComp
												title={tbMenu.id}
												active={currentTab}
												total={
													rfq?.[tbMenu.id]?.meta?.total ||
													rfqBadgeCounts?.[tbMenu.id] ||
													0
												}
											/>
										)}
									</Button>
								</div>
							);
						})}
					</Flex>
					<Flex className="order-1 gap-4 justify-self-end lg:order-2">
						<SearchBar setState={setSearch} />
						<FilterButton
							onClick={() => {
								setModalType("filter");
								onOpen();
							}}
							filters={filters}
							data-testid="filter-button"
						/>
						<SortComp
							data={sortedData}
							sorted={sorted}
							onClickCB={(item) => {
								setOpenPopUp(false);
								setSorted(item);
							}}
							openPopUpCB={openPopUp}
							setOpenPopUpCB={setOpenPopUp}
						></SortComp>
						<ButtonViewGroup
							setView={setView}
							view={view}
						/>
					</Flex>
				</div>
				<FilterRFQ
					isOpen={isOpen}
					onClose={onClose}
				/>
				{pagination}
				{view === "grid" ? (
					<RFQGridView
						currentTab={currentTab}
						currPage={currPage}
						refreshData={refreshData}
						setModalType={setModalType}
						isSidebarCollapsed={isSidebarCollapsed}
						onOpen={onOpen}
					/>
				) : (
					<RFQListView
						currentTab={currentTab}
						currPage={currPage}
						refreshData={refreshData}
						setModalType={setModalType}
						onOpen={onOpen}
					/>
				)}
				{pagination}
				{modalType === "createRFQ" && (
					<CreateRFxSlider
						isOpen={isOpen}
						onClose={onClose}
					/>
				)}
				{modalType === "filter" && (
					<FilterRFQ
						prevValues={prevValues}
						setPrevValues={setPrevValues}
						setFilters={setFilters}
						isOpen={isOpen}
						onClose={onClose}
						currentTab={currentTab}
					/>
				)}
			</div>
		</RFQStyled>
	);
};

const mapStateToProps = (state) => {
	return {
		projects: state.rfq.projects.projects,
		rfq: state.rfq.rfqs.rfqs,
	};
};

export default connect(mapStateToProps, { getRfqPerPage })(RFQ);
